import { isNil, isString } from "@mcwd/typescript-type-guards";
import genericEmailDomainsBlocklist from "../form-logic/genericEmailDomainBlockList.js";
import disposableEmailDomains from "../form-logic/disposableEmailDomains.js";
import { useFormLogger } from "./use-form-logger.js";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionValuesRecord,
  type InjectionKeysRecord,
} from "@mcwd/util-vue";
import { provide } from "vue";

// Message for each validation type
type CheckType = "Disposible Domain" | "Generic Domain" | "Email Format" | "Not String" | "Phone Format" | "Identical Digits";
//return obj for each validation function
export interface ValidationOutput {
  isValid: boolean;
  ValidationType?: CheckType;
}
//validation function type
export type ValidatorFn = (value: unknown) => ValidationOutput;

//type ValidatorFn = (value: unknown) => boolean;
type UseValidationInjectionValues = InjectionValuesRecord<{
  validateEmail: ValidatorFn;
  validateDisposableEmailDomains: ValidatorFn;
  validateGenericBusinessEmail: ValidatorFn;
  validatePhoneNumber: ValidatorFn;
}>;

type UseValidationInjectionKeys = InjectionKeysRecord<UseValidationInjectionValues>;

const UseValidationInjectionKeys = CreateInjectionKeysRecord<UseValidationInjectionKeys>({
  validateEmail: 'validateEmail',
  validateDisposableEmailDomains: 'validateDisposableEmailDomains',
  validateGenericBusinessEmail: 'validateGenericBusinessEmail',
  validatePhoneNumber: 'validatePhoneNumber',
});

export function initValidations() {
  const { logger } = useFormLogger();

  if (checkValuesAlreadyProvided(UseValidationInjectionKeys)) {
    return;
  }
  // Validate email address
  function validateEmail(value: unknown): ValidationOutput {
    if (!isString(value)) {
      return { isValid: false };
    }
    const matchValidEmail = value
      .toLowerCase()
      .match(
        /^([\w\d._\-#])+@([\w\d._\-#]+[.][\w\d._\-#]+)+$/
      );
    const isValid = !isNil(matchValidEmail);
    return { isValid, ValidationType: "Email Format" };
  }
  //helper function for email validation
  function isInBlockedList(nums: string[], target: string): boolean {
    let left = 0;
    let right: number = nums.length - 1;

    while (left <= right) {
      const mid: number = Math.floor((left + right) / 2);

      if (nums[mid] === target) return true;
      if (target < nums[mid]) right = mid - 1;
      else left = mid + 1;
    }

    return false;
  }

  function validateDisposableEmailDomains(value: unknown): ValidationOutput {
    if (!isString(value)) {
      return { isValid: false };
    }

    const isEmailFormatValid = validateEmail(value).isValid;
    if (!isEmailFormatValid) {
      return { isValid: false, ValidationType: "Email Format" };
    }

    const startOfDomain = value.indexOf("@") + 1;
    const domain = value.substring(startOfDomain).toLowerCase();

    const validateDisposableDomain = !isInBlockedList(disposableEmailDomains, domain);
    return { isValid: validateDisposableDomain, ValidationType: "Disposible Domain" };
  }

  function validateGenericBusinessEmail(value: unknown): ValidationOutput {
    if (!isString(value)) {
      return { isValid: false };
    }
    const isEmailFormatValid = validateEmail(value).isValid;
    if (!isEmailFormatValid) {
      return { isValid: false, ValidationType: "Email Format" };
    }

    const validateDisposableDomain = validateDisposableEmailDomains(value);
    if (!validateDisposableDomain.isValid) {
      return { isValid: false, ValidationType: "Disposible Domain" };
    }
    const startOfDomain = value.indexOf("@") + 1;
    const domain = value.substring(startOfDomain).toLowerCase();
    const validateGenericDomain = !isInBlockedList(genericEmailDomainsBlocklist, domain);
    return { isValid: validateGenericDomain, ValidationType: "Generic Domain" };
  }

  function validatePhoneNumber(value: unknown): ValidationOutput {
    if (!isString(value)) {
      logger.debug("Phone Number value is not a string.");
      return { isValid: false };
    }
    //strips out non digit characters
    const OnlyDigits = value.match(/\d/g)?.join("");
    // if phone number is undefined or not long enough its not valid
    if (!OnlyDigits || (OnlyDigits && OnlyDigits.length < 8)) {
      logger.debug("Phone Number does not meet length requirements.");
      return { isValid: false, ValidationType: "Phone Format" };
    }
    //Prevents digits being in ascending/decending order
    //Matches zero or one occurrence of each digit  
    const checkDigitOrder = /^(9?8?7?6?5?4?3?2?1?0?|0?1?2?3?4?5?6?7?8?9?)$/;
    if (checkDigitOrder.test(OnlyDigits)) {
      logger.debug("Phone Number is not valid.");
      return { isValid: false, ValidationType: "Phone Format" };
    }
    if (OnlyDigits && OnlyDigits.split('').every(digit => digit === OnlyDigits![0])) {
      logger.debug("Phone Number has all identical digits.");
      return { isValid: false, ValidationType: "Identical Digits" };
    }
    logger.debug("Phone Number is Valid");
    return { isValid: true };
  }

  provide(UseValidationInjectionKeys.validateDisposableEmailDomains, validateDisposableEmailDomains);
  provide(UseValidationInjectionKeys.validateEmail, validateEmail);
  provide(UseValidationInjectionKeys.validateGenericBusinessEmail, validateGenericBusinessEmail);
  provide(UseValidationInjectionKeys.validatePhoneNumber, validatePhoneNumber);
}

export const useValidations = ({ callerLabel }: { callerLabel?: string } = {}) => {
  const { logger } = useFormLogger();
  try {
    const validateDisposableEmailDomains = injectRequiredWithSelf(UseValidationInjectionKeys.validateDisposableEmailDomains);
    const validateEmail = injectRequiredWithSelf(UseValidationInjectionKeys.validateEmail);
    const validateGenericBusinessEmail = injectRequiredWithSelf(UseValidationInjectionKeys.validateGenericBusinessEmail);
    const validatePhoneNumber = injectRequiredWithSelf(UseValidationInjectionKeys.validatePhoneNumber);
    return { validateDisposableEmailDomains, validateEmail, validateGenericBusinessEmail, validatePhoneNumber };
  } catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
};
